import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  canUsePasskeys,
  signInWithPasskey,
  signInWithPassword,
  verifySignInBackupCode,
  verifySignInTotp,
} from "./api";
import styles from "./AuthScreen.module.css";

export type SignInScreenProps = {
  readonly onAuthenticated: () => void;
};

type SignInStep = "password" | "totp" | "backup";

export function SignInScreen({ onAuthenticated }: SignInScreenProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<SignInStep>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setErrorKey(null);
    try {
      await operation();
    } catch {
      setErrorKey("auth.errors.generic");
    } finally {
      setBusy(false);
    }
  };

  const usePasskey = () => {
    if (!canUsePasskeys()) {
      setErrorKey("auth.errors.passkeyUnavailable");
      return;
    }
    void run(async () => {
      await signInWithPasskey();
      onAuthenticated();
    });
  };

  const submitPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedPassword = password;
    setPassword("");
    void run(async () => {
      const result = await signInWithPassword(email, submittedPassword);
      if (result.secondFactorRequired) {
        setStep("totp");
        return;
      }
      onAuthenticated();
    });
  };

  const submitTotp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedCode = totpCode;
    setTotpCode("");
    void run(async () => {
      await verifySignInTotp(submittedCode);
      onAuthenticated();
    });
  };

  const submitBackupCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedCode = backupCode;
    setBackupCode("");
    void run(async () => {
      await verifySignInBackupCode(submittedCode);
      onAuthenticated();
    });
  };

  return (
    <section className={styles.screen} aria-labelledby="sign-in-title" aria-busy={busy}>
      <div className={styles.card}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>{t("app.name")}</p>
          <h1 id="sign-in-title">{t("auth.signIn.title")}</h1>
          <p>{t("auth.signIn.description")}</p>
        </header>

        {step === "password" ? (
          <>
            <button className={styles.primaryButton} type="button" onClick={usePasskey} disabled={busy}>
              {busy ? t("auth.common.working") : t("auth.signIn.passkey")}
            </button>
            <p className={styles.help} id="passkey-help">
              {t("auth.signIn.passkeyHelp")}
            </p>
            <div className={styles.divider} aria-hidden="true">
              <span>{t("auth.signIn.passwordDivider")}</span>
            </div>
            <form className={styles.form} onSubmit={submitPassword}>
              <label htmlFor="sign-in-email">{t("auth.signIn.email")}</label>
              <input
                className={styles.keyboardSafeControl}
                id="sign-in-email"
                type="email"
                inputMode="email"
                autoComplete="username webauthn"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={320}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={busy}
                required
              />
              <label htmlFor="sign-in-password">{t("auth.signIn.password")}</label>
              <input
                className={styles.keyboardSafeControl}
                id="sign-in-password"
                type="password"
                autoComplete="current-password"
                minLength={12}
                maxLength={128}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={busy}
                required
              />
              <button className={styles.secondaryButton} type="submit" disabled={busy}>
                {busy ? t("auth.common.working") : t("auth.signIn.passwordSubmit")}
              </button>
            </form>
          </>
        ) : step === "totp" ? (
          <form className={styles.form} onSubmit={submitTotp}>
            <h2>{t("auth.signIn.totpTitle")}</h2>
            <p className={styles.help} id="totp-help">{t("auth.signIn.totpHelp")}</p>
            <label htmlFor="sign-in-totp">{t("auth.signIn.totpCode")}</label>
            <input
              className={styles.keyboardSafeControl}
              id="sign-in-totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              minLength={6}
              maxLength={6}
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value)}
              disabled={busy}
              aria-describedby="totp-help"
              required
            />
            <button className={styles.primaryButton} type="submit" disabled={busy}>
              {busy ? t("auth.common.working") : t("auth.signIn.verifyTotp")}
            </button>
            <button className={styles.linkButton} type="button" onClick={() => setStep("backup")} disabled={busy}>
              {t("auth.signIn.useBackupCode")}
            </button>
          </form>
        ) : (
          <form className={styles.form} onSubmit={submitBackupCode}>
            <h2>{t("auth.signIn.backupTitle")}</h2>
            <p className={styles.help} id="backup-help">{t("auth.signIn.backupHelp")}</p>
            <label htmlFor="sign-in-backup">{t("auth.signIn.backupCode")}</label>
            <input
              className={styles.keyboardSafeControl}
              id="sign-in-backup"
              type="text"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={100}
              value={backupCode}
              onChange={(event) => setBackupCode(event.target.value)}
              disabled={busy}
              aria-describedby="backup-help"
              required
            />
            <button className={styles.primaryButton} type="submit" disabled={busy}>
              {busy ? t("auth.common.working") : t("auth.signIn.verifyBackup")}
            </button>
            <button className={styles.linkButton} type="button" onClick={() => setStep("totp")} disabled={busy}>
              {t("auth.signIn.useTotp")}
            </button>
          </form>
        )}

        <div className={styles.messageRegion} data-error={errorKey !== null} aria-live="polite" aria-atomic="true">
          <p className={styles.errorMessage}>{errorKey === null ? "\u00a0" : t(errorKey)}</p>
        </div>
      </div>
    </section>
  );
}
